import { useEffect, useState } from "react";
import api, { apiErr, PDF_BASE } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { fmtMoney, fmtCur, fmtTotals, fmtDateTime } from "@/lib/format";
import { FilePdf, CreditCard, Money, Plus, Bank, ArrowUUpLeft, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const INV_STATUS = { unpaid: { label: "Не оплачено", color: "#FF3B30" }, partial: { label: "Частково", color: "#FFCC00" }, paid: { label: "Оплачено", color: "#34C759" } };

function StatCard({ label, data, color = "", testid }) {
  const entries = Object.entries(data || {}).filter(([, v]) => v);
  return (
    <Card className="p-5 min-w-0">
      <div className="text-sm text-muted-foreground">{label}</div>
      {entries.length === 0 ? (
        <div className={`font-head font-black text-xl lg:text-2xl mt-1 ${color}`} data-testid={testid}>{fmtCur(0)}</div>
      ) : (
        <div className={`mt-1 flex flex-col gap-y-0.5 ${color}`} data-testid={testid}>
          {entries.map(([c, v]) => (
            <span key={c} className="font-head font-black text-xl lg:text-2xl leading-tight whitespace-nowrap">{fmtCur(v, c)}</span>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function Finance() {
  const [invoices, setInvoices] = useState([]);
  const [cashbox, setCashbox] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [activeInv, setActiveInv] = useState(null);
  const [pay, setPay] = useState({ amount: "", method: "cash", currency: "UAH" });
  const [expOpen, setExpOpen] = useState(false);
  const [exp, setExp] = useState({ amount: "", description: "", method: "cash", currency: "UAH" });
  const [wdOpen, setWdOpen] = useState(false);
  const [wd, setWd] = useState({ cash: "", card: "", currency: "UAH", note: "" });
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const refundInvoice = async (inv) => {
    if (!window.confirm(`Повернути всі оплати за рахунком ${inv.number}? Кошти буде списано з каси.`)) return;
    try { await api.post(`/invoices/${inv.id}/refund`); toast.success("Оплату повернено"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Видалити рахунок ${inv.number}? Повʼязані транзакції каси буде видалено. Цю дію не можна скасувати.`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Рахунок видалено"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const clearCashbox = async () => {
    if (!window.confirm("Очистити касу? Усі транзакції каси (оплати, витрати, виводи) буде видалено. Цю дію не можна скасувати.")) return;
    try { const r = await api.post("/cashbox/clear"); toast.success(`Касу очищено (${r.data.deleted} транзакцій)`); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const load = () => { api.get("/invoices").then((r) => setInvoices(r.data)); api.get("/cashbox").then((r) => setCashbox(r.data)); };
  useEffect(() => { load(); }, []);

  const openPay = (inv) => {
    const entries = Object.entries(inv.balance_by_currency || {}).filter(([, v]) => v > 0);
    const [c, bal] = entries[0] || ["UAH", inv.balance];
    setActiveInv(inv);
    setPay({ amount: String(bal), method: "cash", currency: c });
    setPayOpen(true);
  };
  const addPayment = async () => {
    try {
      await api.post(`/invoices/${activeInv.id}/payments`, { amount: Number(pay.amount), method: pay.method, currency: pay.currency });
      toast.success("Оплату прийнято"); setPayOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const addExpense = async () => {
    if (!exp.amount || !exp.description) return toast.error("Заповніть суму та опис");
    try { await api.post("/cashbox/expense", { ...exp, amount: Number(exp.amount) }); toast.success("Витрату додано"); setExpOpen(false); setExp({ amount: "", description: "", method: "cash", currency: "UAH" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const doWithdrawal = async () => {
    const cash = Number(wd.cash) || 0, card = Number(wd.card) || 0;
    if (cash <= 0 && card <= 0) return toast.error("Вкажіть суму виводу з каси або картки");
    try {
      await api.post("/cashbox/withdrawal", { cash, card, currency: wd.currency, note: wd.note });
      toast.success("Вивід коштів проведено"); setWdOpen(false); setWd({ cash: "", card: "", currency: "UAH", note: "" }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Фінанси" subtitle="Рахунки, оплати, каса" />

      {cashbox && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <StatCard label="Баланс каси" data={cashbox.balance_by_currency} />
          <StatCard label="Готівка" data={cashbox.cash_by_currency} color="text-green-500" />
          <StatCard label="Картка" data={cashbox.card_by_currency} color="text-blue-400" />
          <StatCard label="Не оплачено" data={cashbox.unpaid_by_currency} color="text-yellow-500" testid="unpaid-total" />
          <StatCard label="Витрати" data={cashbox.expense_by_currency} color="text-destructive" />
        </div>
      )}

      <Tabs defaultValue="invoices">
        <TabsList className="mb-4">
          <TabsTrigger value="invoices" data-testid="tab-invoices">Рахунки</TabsTrigger>
          <TabsTrigger value="cashbox" data-testid="tab-cashbox">Каса</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices">
          <Card>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Рахунок</TableHead><TableHead>Клієнт</TableHead><TableHead>Наряд</TableHead>
                <TableHead className="text-right">Сума</TableHead><TableHead className="text-right">Оплачено</TableHead>
                <TableHead>Статус</TableHead><TableHead className="text-right">Дії</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {invoices.map((inv) => {
                  const st = INV_STATUS[inv.status];
                  return (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.number}`}>
                      <TableCell className="font-semibold">{inv.number}</TableCell>
                      <TableCell>{inv.client_name}</TableCell>
                      <TableCell className="text-muted-foreground">{inv.order_number}</TableCell>
                      <TableCell className="text-right font-semibold">{inv.totals_by_currency ? fmtTotals(inv.totals_by_currency) : fmtMoney(inv.total)}</TableCell>
                      <TableCell className="text-right">{inv.paid_by_currency && Object.keys(inv.paid_by_currency).length ? fmtTotals(inv.paid_by_currency) : fmtMoney(inv.paid)}</TableCell>
                      <TableCell><span className="text-xs px-2 py-0.5 rounded-full" style={{ background: st.color + "22", color: st.color }}>{st.label}</span></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <button className="text-muted-foreground hover:text-foreground p-1" onClick={() => window.open(`${PDF_BASE}/invoices/${inv.id}/pdf`, "_blank")} data-testid={`invoice-pdf-${inv.number}`}><FilePdf size={18} /></button>
                        {inv.status !== "paid" && <Button size="sm" variant="outline" className="ml-1" onClick={() => openPay(inv)} data-testid={`pay-invoice-${inv.number}`}>Оплата</Button>}
                        {isAdmin && inv.status !== "unpaid" && (
                          <button className="text-muted-foreground hover:text-yellow-600 p-1 ml-1" title="Повернення оплати" onClick={() => refundInvoice(inv)} data-testid={`refund-invoice-${inv.number}`}><ArrowUUpLeft size={18} /></button>
                        )}
                        {isAdmin && (
                          <button className="text-muted-foreground hover:text-destructive p-1 ml-1" title="Видалити рахунок" onClick={() => deleteInvoice(inv)} data-testid={`delete-invoice-${inv.number}`}><Trash size={18} /></button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {invoices.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Немає рахунків. Виставте рахунок зі сторінки наряду.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="cashbox">
          <div className="flex justify-end gap-2 mb-4">
            {isAdmin && (
              <Button variant="outline" className="text-destructive hover:text-destructive" onClick={clearCashbox} data-testid="clear-cashbox-btn"><Trash size={18} className="mr-1" /> Очистити касу</Button>
            )}
            <Dialog open={wdOpen} onOpenChange={setWdOpen}>
              <DialogTrigger asChild><Button variant="outline" data-testid="withdraw-btn"><Bank size={18} className="mr-1" /> Вивід коштів</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Вивід коштів</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Валюта</Label>
                    <Select value={wd.currency} onValueChange={(v) => setWd({ ...wd, currency: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="withdraw-currency"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="UAH">₴ ГРН</SelectItem><SelectItem value="USD">$ USD</SelectItem><SelectItem value="EUR">€ EUR</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="flex items-center gap-1"><Money size={16} /> З каси (готівка)</Label><Input type="number" value={wd.cash} onChange={(e) => setWd({ ...wd, cash: e.target.value })} placeholder="0" className="mt-1.5" data-testid="withdraw-cash" /></div>
                    <div><Label className="flex items-center gap-1"><CreditCard size={16} /> З картки</Label><Input type="number" value={wd.card} onChange={(e) => setWd({ ...wd, card: e.target.value })} placeholder="0" className="mt-1.5" data-testid="withdraw-card" /></div>
                  </div>
                  <div><Label>Коментар</Label><Input value={wd.note} onChange={(e) => setWd({ ...wd, note: e.target.value })} placeholder="Вивід коштів" className="mt-1.5" data-testid="withdraw-note" /></div>
                </div>
                <DialogFooter><Button onClick={doWithdrawal} data-testid="save-withdraw-btn">Провести</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={expOpen} onOpenChange={setExpOpen}>
              <DialogTrigger asChild><Button variant="outline" data-testid="add-expense-btn"><Plus size={18} className="mr-1" /> Витрата</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Додати витрату</DialogTitle></DialogHeader>
                <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Сума</Label><Input type="number" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} className="mt-1.5" data-testid="expense-amount" /></div>
                  <div>
                    <Label>Валюта</Label>
                    <Select value={exp.currency} onValueChange={(v) => setExp({ ...exp, currency: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="expense-currency"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="UAH">₴ ГРН</SelectItem><SelectItem value="USD">$ USD</SelectItem><SelectItem value="EUR">€ EUR</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Опис</Label><Input value={exp.description} onChange={(e) => setExp({ ...exp, description: e.target.value })} className="mt-1.5" data-testid="expense-desc" /></div>
                <div>
                  <Label>Звідки списати</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    <button onClick={() => setExp({ ...exp, method: "cash" })} data-testid="expense-cash"
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-md border transition-colors ${exp.method === "cash" ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                      <Money size={18} /> Готівка
                    </button>
                    <button onClick={() => setExp({ ...exp, method: "card" })} data-testid="expense-card"
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-md border transition-colors ${exp.method === "card" ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                      <CreditCard size={18} /> Картка
                    </button>
                  </div>
                </div>
                </div>
                <DialogFooter><Button onClick={addExpense} data-testid="save-expense-btn">Зберегти</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card>
            <Table>
              <TableHeader><TableRow><TableHead>Дата</TableHead><TableHead>Опис</TableHead><TableHead>Спосіб</TableHead><TableHead className="text-right">Сума</TableHead></TableRow></TableHeader>
              <TableBody>
                {cashbox?.transactions.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-muted-foreground">{fmtDateTime(t.created_at)}</TableCell>
                    <TableCell>{t.description}</TableCell>
                    <TableCell>{t.method === "cash" ? "Готівка" : "Картка"}</TableCell>
                    <TableCell className={`text-right font-semibold ${t.type === "income" ? "text-green-500" : "text-destructive"}`}>{t.type === "income" ? "+" : "−"}{fmtCur(t.amount, t.currency)}</TableCell>
                  </TableRow>
                ))}
                {(!cashbox || cashbox.transactions.length === 0) && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Немає транзакцій</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Прийом оплати — {activeInv?.number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">До сплати: <span className="font-semibold text-foreground">{fmtCur((activeInv?.balance_by_currency || {})[pay.currency] ?? 0, pay.currency)}</span></div>
            <div>
              <Label>Валюта</Label>
              <Select value={pay.currency} onValueChange={(c) => setPay((p) => ({ ...p, currency: c, amount: String((activeInv?.balance_by_currency || {})[c] ?? "") }))}>
                <SelectTrigger className="mt-1.5" data-testid="payment-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(activeInv?.totals_by_currency || { UAH: 0 }).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Сума</Label><Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-1.5" data-testid="payment-amount" /></div>
            <div>
              <Label>Спосіб оплати</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button onClick={() => setPay({ ...pay, method: "cash" })} data-testid="pay-cash"
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-md border transition-colors ${pay.method === "cash" ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                  <Money size={18} /> Готівка
                </button>
                <button onClick={() => setPay({ ...pay, method: "card" })} data-testid="pay-card"
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-md border transition-colors ${pay.method === "card" ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                  <CreditCard size={18} /> Картка
                </button>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={addPayment} data-testid="confirm-payment-btn">Прийняти оплату</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
